// Fase 3: ficha do edital (leitura por IA), fit por cliente e triagem da equipe.
import { z } from 'zod';
import { Pipeline } from './pipeline';

export const FIT_DECISOES = ['recomendado', 'possivel', 'descartado'] as const;
export const TRIAGEM_DECISOES = ['aprovado', 'descartado', 'adiado'] as const;
export const FICHA_STATUSES = ['pendente', 'enviada', 'gerada', 'erro'] as const;

/** Saída estruturada da leitura do edital. É o que a IA devolve e o que o backoffice mostra. */
export const Ficha = z.object({
  resumo: z.string().describe('O que o órgão quer comprar, em 2 ou 3 frases, linguagem direta'),
  categoria: z
    .enum([
      'software_licenca',
      'software_desenvolvimento',
      'software_locacao_saas',
      'servico_ti',
      'suporte_manutencao',
      'dados_ia',
      'infraestrutura_nuvem',
      'consultoria',
      'misto',
      'fora_de_ti',
    ])
    .describe('Tipo principal da contratação'),
  entregaveis: z.array(z.string()).describe('Entregáveis ou serviços principais, um por item'),
  tecnologias: z.array(z.string()).describe('Tecnologias, linguagens, plataformas ou padrões citados'),
  habilitacao: z.object({
    atestados: z.array(z.string()).describe('Atestados de capacidade técnica exigidos, com quantidades ou valores mínimos quando houver'),
    certificacoes: z.array(z.string()).describe('Certificações exigidas da empresa ou da equipe'),
    equipeMinima: z.array(z.string()).describe('Perfis profissionais exigidos e requisitos de formação ou experiência'),
    financeira: z.array(z.string()).describe('Exigências econômico-financeiras: índices, capital social, patrimônio líquido'),
    outras: z.array(z.string()).describe('Outras exigências: visita técnica, amostra, prova de conceito, registro em conselho'),
  }),
  execucao: z.object({
    local: z.string().nullable().describe('Onde o serviço é executado; null se não dito'),
    presencial: z.enum(['sim', 'nao', 'parcial', 'nao_informado']).describe('Exige presença física da equipe?'),
    prazoMeses: z.number().int().nullable().describe('Vigência ou prazo de execução em meses; null se não dito'),
  }),
  exclusivoMePp: z.boolean().nullable().describe('Participação exclusiva ou com benefício para ME/EPP? null se não dito'),
  riscos: z.array(z.string()).describe('Pontos de atenção: prazos curtos, exigências incomuns, penalidades pesadas, orçamento sigiloso'),
  perguntasAbertas: z.array(z.string()).describe('O que o texto não deixa claro e vale confirmar no edital ou com o órgão'),
  confianca: z.number().min(0).max(1).describe('0 a 1: quão completo e legível estava o material lido'),
});
export type Ficha = z.infer<typeof Ficha>;

/** Saída estruturada do fit: a ficha cruzada com o perfil de um cliente. */
export const FitAnalise = z.object({
  nota: z.number().int().min(0).max(100).describe('0 a 100: chance de a empresa conseguir participar e ser competitiva'),
  decisao: z.enum(FIT_DECISOES).describe('recomendado: vale entrar; possivel: dá com esforço ou parceiro; descartado: não cabe'),
  resumo: z.string().describe('Uma frase para a analista: por que esta nota'),
  pontosFortes: z.array(z.string()).describe('O que no perfil da empresa atende bem ao edital'),
  lacunas: z.array(z.string()).describe('Exigências que a empresa não atende ou não comprova hoje'),
  acoes: z.array(z.string()).describe('O que fazer para participar: buscar parceiro, atestado, certidão, visita técnica'),
});
export type FitAnalise = z.infer<typeof FitAnalise>;

const editalResumo = z.object({
  id: z.string().uuid(),
  numeroControlePncp: z.string(),
  objeto: z.string(),
  modalidadeNome: z.string(),
  orgao: z.string(),
  uf: z.string().nullable(),
  municipio: z.string().nullable(),
  valorEstimadoMinor: z.number().nullable(),
  encerramentoPropostasEm: z.string().nullable(),
  publicadoEm: z.string().nullable(),
});

export const FitItem = z.object({
  id: z.string().uuid(),
  cliente: z.object({ id: z.string().uuid(), nome: z.string() }),
  edital: editalResumo,
  nota: z.number().nullable(),
  decisao: z.enum(FIT_DECISOES).nullable(),
  analise: FitAnalise.nullable(),
  geradoEm: z.string().nullable(),
  triagem: z
    .object({ decisao: z.enum(TRIAGEM_DECISOES), observacao: z.string().nullable(), analista: z.string(), em: z.string() })
    .nullable(),
  alertaEnviadoEm: z.string().nullable(),
  /** Resposta do cliente no app (a última). */
  interesse: z.object({ interesse: z.enum(['participar', 'nao', 'ajuda']), observacao: z.string().nullable(), em: z.string() }).nullable(),
  pipeline: Pipeline,
});
export type FitItem = z.infer<typeof FitItem>;

export const FitsQuery = z.object({
  clienteId: z.string().uuid().optional(),
  decisao: z.enum(FIT_DECISOES).optional(),
  /** `pendente` = sem triagem; `todas` inclui triadas. */
  triagem: z.enum(['pendente', 'aprovado', 'descartado', 'adiado', 'todas']).default('pendente'),
  /** Só editais com propostas ainda abertas (padrão). */
  abertos: z.coerce.boolean().default(true),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(10).max(100).default(25),
});
export const FitsResponse = z.object({ itens: z.array(FitItem), total: z.number(), pagina: z.number(), porPagina: z.number() });

export const TriagemInput = z.object({
  decisao: z.enum(TRIAGEM_DECISOES),
  observacao: z.string().trim().max(2000).nullable().default(null),
});

export const EditalFichaResponse = z.object({
  status: z.enum(FICHA_STATUSES).nullable(),
  ficha: Ficha.nullable(),
  geradaEm: z.string().nullable(),
  erro: z.string().nullable(),
  modelo: z.string().nullable(),
  fits: z.array(FitItem),
});

/** Painel da IA (custos e filas) para a tela Sincronização. */
export const IaStatusResponse = z.object({
  fichas: z.record(z.string(), z.number()),
  fits: z.record(z.string(), z.number()),
  lotes: z.array(
    z.object({
      id: z.string().uuid(),
      tipo: z.enum(['ficha', 'fit']),
      status: z.enum(['enviado', 'concluido', 'erro']),
      pedidos: z.number(),
      sucessos: z.number(),
      falhas: z.number(),
      tokensEntrada: z.number(),
      tokensSaida: z.number(),
      custoUsd: z.number(),
      enviadoEm: z.string(),
      concluidoEm: z.string().nullable(),
    }),
  ),
  custoUsd30Dias: z.number(),
});
